/**
 * WorkspaceCreateSheet.js
 *
 * "New workspace" sheet: pick how to start - blank (asks for a name), from a
 * role template, or with AI. The template and AI flows open their own sheets.
 *
 * @module components/workspaces/WorkspaceCreateSheet
 */

import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PremiumSheet, {SheetInput, SheetLabel} from '../management/PremiumSheet';
import {SheetRow} from './WorkspaceUI';

/**
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {() => void} props.onClose Dismiss.
 * @param {(name: string) => void} props.onBlank Create a blank workspace.
 * @param {() => void} props.onTemplates Open the template picker.
 * @param {() => void} props.onAi Open the AI generator.
 * @returns {JSX.Element} The sheet.
 */
export default function WorkspaceCreateSheet({visible, onClose, onBlank, onTemplates, onAi}) {
    const {t} = useTranslation();
    const [blank, setBlank] = useState(false);
    const [name, setName] = useState('');

    useEffect(() => {
        if (visible) {
            setBlank(false);
            setName('');
        }
    }, [visible]);

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('wsd_new_workspace')}
            icon="plus-box-outline"
            hideFooter={!blank}
            confirmLabel={t('wsd_create')}
            confirmIcon="check"
            confirmDisabled={!name.trim()}
            onConfirm={() => onBlank(name)}
            onCancel={() => setBlank(false)}
        >
            {blank ? (
                <>
                    <SheetLabel>{t('wsd_workspace_name')}</SheetLabel>
                    <SheetInput
                        autoFocus
                        value={name}
                        onChangeText={setName}
                        placeholder={t('wsd_workspace_name_placeholder')}
                        maxLength={40}
                        returnKeyType="done"
                        onSubmitEditing={() => name.trim() && onBlank(name)}
                    />
                </>
            ) : (
                <>
                    <SheetRow icon="view-grid-plus-outline" title={t('wsd_from_template')} subtitle={t('wsd_from_template_desc')} onPress={onTemplates} />
                    <SheetRow icon="creation" title={t('wsd_with_ai')} subtitle={t('wsd_with_ai_desc')} tone="violet" onPress={onAi} />
                    <SheetRow icon="tab-plus" title={t('wsd_blank')} subtitle={t('wsd_blank_desc')} tone="neutral" onPress={() => setBlank(true)} />
                </>
            )}
        </PremiumSheet>
    );
}
